import { TrashBin } from "@gravity-ui/icons";
import { Button, Flex, Icon, Spin, Text } from "@gravity-ui/uikit";
import { useCallback } from "react";
import { useTranslate } from "src/i18n";
import { formatBytes } from "src/util";
import { ShowConfirmDialog } from "src/widgets/Alert/Alert";

import {
  DRAG_MIME_PLAIN,
  DRAG_MIME_URI_LIST,
} from "../WatermarkOptionsForm.constants";
import css from "./WatermarkGallery.module.scss";

export interface GalleryItem {
  id: string;
  url: string;
  filename: string;
  size: number;
  mimeType: string;
  uploadedAt: string;
  isUploading?: boolean;
  uploadProgress?: number;
  uploadError?: string | null;
}

interface WatermarkGalleryProps {
  items: GalleryItem[];
  selectedUrl?: string;
  onSelect: (url: string) => void;
  onDelete: (id: string) => void;
  onRetry?: (id: string) => void;
}

export const WatermarkGallery = ({
  items,
  selectedUrl,
  onSelect,
  onDelete,
  onRetry,
}: WatermarkGalleryProps) => {
  const { t } = useTranslate();

  const handleDelete = useCallback(
    async (item: GalleryItem) => {
      const confirmed = await ShowConfirmDialog(
        t(
          "watermark.deleteConfirmMessage",
          "Удалить {{filename}}? Это действие нельзя отменить.",
          { filename: item.filename },
        ),
        t("watermark.deleteConfirmTitle", "Удаление watermark"),
      );
      if (confirmed) onDelete(item.id);
    },
    [onDelete, t],
  );

  if (items.length === 0) return null;

  return (
    <div className={css.gallery}>
      {items.map((item) => {
        const isSelected = selectedUrl === item.url;
        const hasError = !!item.uploadError;
        const isBusy = !!item.isUploading;
        const canInteract = !isBusy && !hasError;
        const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
          e.dataTransfer.effectAllowed = "copy";
          e.dataTransfer.setData(DRAG_MIME_URI_LIST, item.url);
          e.dataTransfer.setData(DRAG_MIME_PLAIN, item.url);
        };
        return (
          <div
            key={item.id}
            className={`${css.tile} ${isSelected ? css.selected : ""} ${isBusy ? css.uploading : ""} ${hasError ? css.error : ""}`}
            onClick={() => {
              if (canInteract) onSelect(item.url);
            }}
            role="radio"
            aria-checked={isSelected}
            aria-label={t("watermark.selectAriaLabel", "Выбрать {{filename}}", {
              filename: item.filename,
            })}
            tabIndex={isBusy || hasError ? -1 : 0}
            draggable={canInteract}
            onDragStart={handleDragStart}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                if (canInteract) onSelect(item.url);
              }
            }}
          >
            <div className={css.imageWrapper}>
              <img
                src={item.url}
                alt={item.filename}
                className={css.thumbnail}
                draggable={false}
              />
              {isBusy && (
                <div className={css.overlay}>
                  <Spin size="s" />
                  {item.uploadProgress !== undefined &&
                    item.uploadProgress > 0 && (
                      <Text variant="caption-2" color="light-primary">
                        {item.uploadProgress}%
                      </Text>
                    )}
                </div>
              )}
              {hasError && (
                <div className={css.overlayError}>
                  <Text variant="caption-2" color="light-primary">
                    {item.uploadError}
                  </Text>
                  {onRetry && (
                    <Button
                      view="flat-contrast"
                      size="s"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRetry(item.id);
                      }}
                    >
                      {t("watermark.retry", "Повторить")}
                    </Button>
                  )}
                </div>
              )}
              {!isBusy && !hasError && (
                <button
                  className={css.deleteBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(item);
                  }}
                  title={t("watermark.deleteTitle", "Удалить")}
                  aria-label={t(
                    "watermark.deleteAriaLabel",
                    "Удалить {{filename}}",
                    {
                      filename: item.filename,
                    },
                  )}
                >
                  <Icon data={TrashBin} size={14} />
                </button>
              )}
            </div>
            <Flex direction="column" gap={0.5} className={css.meta}>
              <Text
                variant="caption-2"
                className={css.filename}
                title={item.filename}
              >
                {item.filename}
              </Text>
              <Text variant="caption-2" color="secondary">
                {formatBytes(item.size)}
              </Text>
              {hasError && (
                <Text
                  variant="caption-2"
                  color="danger"
                  className={css.errorText}
                >
                  {item.uploadError}
                </Text>
              )}
            </Flex>
          </div>
        );
      })}
    </div>
  );
};
